import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { Delete, Edit, RemoveRedEye } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { useGetGalleriesQuery, useDeleteGalleryMutation } from "./api/galleriesApi";
import { Gallery } from "./types/gallery";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditGallery from "./components/AddEditGallery";
import CategoryManager from "./components/CategoryManager";
import { TabView } from "../../libs/core/components/TabView";

const GalleryManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null);
  const navigate = useNavigate();

  const { data: galleries, isLoading, isError, isFetching } = useGetGalleriesQuery({
    page: pagination.pageIndex + 1,
    per_page: pagination.pageSize,
  });
  const [deleteGallery] = useDeleteGalleryMutation();

  const columns: Array<MRT_ColumnDef<Gallery>> = [
    {
      header: "Image",
      accessorKey: "images",
      size: 80,
      Cell({ row: { original } }) {
        const firstImage = original.images && original.images.length > 0 ? original.images[0] : null;
        return firstImage ? (
          <img
            src={firstImage}
            alt={original.title}
            width={60}
            height={60}
            style={{ borderRadius: 6 }}
          />
        ) : (
          <Box
            width={60}
            height={60}
            sx={{
              backgroundColor: 'grey.200',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No Image
            </Typography>
          </Box>
        );
      },
    },
    {
      header: "Title",
      accessorKey: "title",
      size: 150,
    },
    {
      header: "Description",
      accessorKey: "description",
      size: 200,
      Cell({ row: { original } }) {
        const description = original.description || "";
        return (
          <Typography variant="body2" sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 200
          }}>
            {description.substring(0, 100)}...
          </Typography>
        );
      },
    },
    {
      header: "Category",
      accessorKey: "category",
      size: 120,
      Cell({ row: { original } }) {
        return original.category ? (
          <Chip 
            label={original.category.name} 
            color="primary" 
            size="small" 
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No category
          </Typography>
        );
      },
    },
    {
      header: "Featured",
      accessorKey: "is_featured",
      size: 100,
      Cell({ row: { original } }) {
        return (
          <Chip 
            label={original.is_featured ? "Featured" : "Regular"} 
            color={original.is_featured ? "success" : "default"} 
            size="small" 
          />
        );
      },
    },
    {
      header: "Created At",
      accessorKey: "created_at",
      size: 120,
      Cell({ row: { original } }) {
        return new Date(original.created_at).toLocaleDateString();
      },
    },
    {
      header: "Actions",
      id: "actions",
      size: 120,
      Cell({ row: { original } }) {
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/galleries/${original.id}`);
              }}
              color="primary"
            >
              <RemoveRedEye />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedGallery(original);
                setOpenDialog(true);
              }}
              color="primary"
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedGallery(original);
                setOpenConfirmModal(true);
              }}
              color="error"
            >
              <Delete />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleDelete = async () => {
    if (selectedGallery) {
      try {
        await deleteGallery(selectedGallery.id.toString()).unwrap();
        setOpenConfirmModal(false);
        setSelectedGallery(null);
      } catch (error) {
        console.error("Failed to delete gallery:", error);
      }
    }
  };

  const handleSuccess = () => {
    setSelectedGallery(null);
  };

  return (
    <Box>
      <TabView
        tabs={[
          {
            label: "Galleries",
            body: (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h4">Gallery Management</Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setSelectedGallery(null);
                      setOpenDialog(true);
                    }}
                  >
                    Add New Gallery
                  </Button>
                </Box>

                <DataTable
                  columns={columns}
                  list={galleries?.data?.data ?? galleries?.data ?? []}
                  count={galleries?.data?.total ?? (galleries?.data?.length ?? 0)}
                  getRowId={(row) => row?.id?.toString() || Math.random().toString()}
                  isLoading={isLoading}
                  isSuccess={!isError}
                  isError={isError}
                  isFetching={isFetching}
                  pagination={pagination}
                  onPaginationChange={setPagination}
                  enableRowSelection={false}
                  onRowClick={(row) => navigate(`/galleries/${row.id}`)}
                />

                <AddEditGallery
                  open={openDialog}
                  setModalOpen={setOpenDialog}
                  onSuccess={handleSuccess}
                  gallery={selectedGallery}
                />

                <ConfirmModal
                  modalOpen={openConfirmModal}
                  setModalOpen={setOpenConfirmModal}
                  onOk={handleDelete}
                  title="Delete Gallery"
                  message={`Are you sure you want to delete "${selectedGallery?.title}"? This action cannot be undone.`}
                  type="warning"
                />
              </Box>
            ),
          },
          {
            label: "Categories",
            body: <CategoryManager />,
          },
        ]}
      />
    </Box>
  );
};

export default GalleryManager;
